<script setup lang="ts">
import AppState, { getRoomsInLocation, getTargetTimezone } from '@/states/app'
import {
  CalendarCheck01Icon,
  Pencil02Icon,
  PlusIcon,
  Toggle03LeftIcon,
  Toggle03RightIcon,
  Trash01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIActionDropdown,
  UIButton,
  UIEmpty,
  UITable,
  UITag,
  UITooltip,
  renderIcon,
  useNotification,
} from '@gohighlevel/ghl-ui'
import { h, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ResourceList } from '../../class/pages/ResourceList'
import { CalendarResourceService } from '../../class/services/CalendarResourceService'
import {
  CALENDAR_RESOURCE_TYPES,
  CalendarResource,
} from '../../models/calendar_resource'
import CreateRoomModal from './CreateRoomModal.vue'

import DeleteConfirmation from '@/components/common/DeleteConfirmation.vue'
import GeneralConfirmation from '@/components/common/GeneralConfirmation.vue'
import moment from 'moment-timezone'
import SearchBox from '../common/SearchBox.vue'

const { t } = useI18n()

const tableActionsOptionsForActiveRooms = [
  {
    label: t('settings.calendar.edit'),
    key: 'edit',
    icon: renderIcon(Pencil02Icon),
  },
  {
    label: t('settings.calendar.rooms_disable'),
    key: 'disable',
    icon: renderIcon(Toggle03RightIcon),
  },
  {
    label: t('settings.calendar.delete_group'),
    key: 'delete_room',
    icon: renderIcon(Trash01Icon),
  },
]

const tableActionsOptionsForDisabledRooms = [
  ...tableActionsOptionsForActiveRooms,
]
tableActionsOptionsForDisabledRooms[1] = {
  label: t('settings.calendar.rooms_activate'),
  key: 'disable',
  icon: renderIcon(Toggle03LeftIcon),
}

const targetTimezone = getTargetTimezone()

const columns = [
  {
    title: t('settings.calendar.rooms_name'),
    key: 'name',
  },
  {
    title: t('settings.calendar.rooms_capacity'),
    key: 'capacity',
  },
  {
    title: t('settings.calendar.calendar_list'),
    key: 'calendars_tags',
    render(row) {
      const tags: any = []
      const calendarIds = row.calendarIds
      if (!calendarIds?.length) {
        return
      }
      const activeCalendars = calendarIds.filter(
        e => !roomList.isCalendarDeleted(e)
      )
      for (let i = 0; i < 2; i++) {
        if (activeCalendars[i]) {
          const calendarName = roomList.getCalName(activeCalendars[i])
          const truncatedName =
            calendarName.length > 15
              ? calendarName.substring(0, 15) + '...'
              : calendarName

          tags.push(
            h(
              UITooltip,
              { placement: 'top' },
              {
                trigger: () =>
                  h(
                    UITag,
                    {
                      type: 'info',
                      id: activeCalendars[i],
                      class: 'cursor-pointer',
                    },
                    truncatedName
                  ),
                default: () =>
                  h('div', { class: 'max-w-[280px] w-auto' }, calendarName),
              }
            )
          )
        }
      }
      if (activeCalendars.length > 2) {
        const additionalCalendars = activeCalendars
          .slice(2)
          .map(id => roomList.getCalName(id))

        tags.push(
          h(
            UITooltip,
            { placement: 'top' },
            {
              trigger: () =>
                h(
                  UITag,
                  {
                    id: `count`,
                    class: 'cursor-pointer',
                  },
                  `+${activeCalendars.length - 2}`
                ),
              default: () =>
                h(
                  'div',
                  {
                    class: 'max-w-[180px] mb-[5px]',
                    style: { fontFamily: 'Inter !important' },
                  },
                  additionalCalendars.map((name, index) =>
                    h(
                      'div',
                      { class: 'max-w-[280px] w-auto break-words' },
                      index + 1 + '. ' + name
                    )
                  )
                ),
            }
          )
        )
      }
      return h(
        'div',
        {
          style: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
          },
        },
        tags
      )
    },
  },
  {
    title: t('settings.calendar.status'),
    key: 'isActive',
    render(room) {
      return h('div', [
        h(
          UITag,
          {
            type: room.isActive ? 'success' : 'error',
            bordered: false,
            size: 'medium',
          },
          room.isActive
            ? t('settings.calendar.active')
            : t('settings.calendar.inactive')
        ),
      ])
    },
  },
  {
    title: t('settings.calendar.date_updated'),
    key: 'dateUpdated',
    render(room) {
      return h('div', { class: 'flex flex-col gap-1 items-start' }, [
        h(
          'div',
          {
            class: 'm-0 text-[13px] font-normal text-gray-600',
          },
          moment(room.updatedAt).tz(targetTimezone).format('MMM DD YYYY')
        ),
        h(
          'h6',
          {
            class: 'm-0 text-[13px] font-normal text-gray-400',
          },
          `${moment(room.updatedAt).tz(targetTimezone).format('hh:mm A')}`
        ),
      ])
    },
  },
  {
    title: t('settings.calendar.action_dropdown'),
    key: 'actions',
    render(row) {
      return h(
        UIActionDropdown,
        {
          options: row.isActive
            ? tableActionsOptionsForActiveRooms.filter(
                x => x.key !== 'rearrange_calendars' || row.nosOfCalendars > 0
              )
            : tableActionsOptionsForDisabledRooms.filter(
                x => x.key !== 'rearrange_calendars' || row.nosOfCalendars > 0
              ),
          onSelect: val => dropDownActionChange(val, row),
          id: 'room-' + row._id,
        },
        {}
      )
    },
  },
]

const selectedRoom = ref()
const isProcessing = ref(false)
const showDeleteConfirmationModal = ref(false)
const showNewRoomModal = ref(false)
const notification = useNotification()
const roomList = new ResourceList(CALENDAR_RESOURCE_TYPES.ROOMS)
const showDisableConfirmationModal = ref(false)
const roomEditPayload = ref()

const dropDownActionChange = (action, room) => {
  selectedRoom.value = room
  if (action === 'edit') {
    openRoomsModal(room)
  } else if (action === 'disable') {
    showDisableConfirmationModal.value = true
  } else if (action === 'delete_room') {
    showDeleteConfirmationModal.value = true
  }
}

onMounted(async () => {
  await getRoomsInLocation()
})

const closeModal = () => {
  showDisableConfirmationModal.value = false
  showNewRoomModal.value = false
  showDeleteConfirmationModal.value = false
  isProcessing.value = false
}

const openRoomsModal = (room: CalendarResource | null) => {
  showNewRoomModal.value = true
  roomEditPayload.value = room
}

const openNewRoomsModal = () => {
  showNewRoomModal.value = true
  roomEditPayload.value = null
}

const showInfoNotification = (message: string) => {
  notification.info({
    content: message,
    duration: 1 * 1000,
  })
}

// API calls start
const runPostApiCallsCleanUps = async () => {
  closeModal()
  await getRoomsInLocation(true)
}

const deleteRoom = async () => {
  if (!selectedRoom.value) {
    return
  }
  isProcessing.value = true
  await CalendarResourceService.delete(
    selectedRoom.value._id,
    CALENDAR_RESOURCE_TYPES.ROOMS
  )
  showInfoNotification(`Room deleted`)
  await runPostApiCallsCleanUps()
}

const disableRoom = async () => {
  if (!selectedRoom.value) {
    return
  }
  isProcessing.value = true
  await CalendarResourceService.update(
    selectedRoom.value._id,
    { isActive: !selectedRoom.value.isActive },
    CALENDAR_RESOURCE_TYPES.ROOMS
  )
  showInfoNotification(
    `Room ${selectedRoom.value.isActive ? 'Disabled' : 'Activated'}`
  )
  await runPostApiCallsCleanUps()
}

const onSearch = (value: string) => {
  roomList.filters.value.searchText = value
}

// API calls end
</script>
<template>
  <div id="roomsListContainer">
    <CreateRoomModal
      :show="showNewRoomModal"
      :edit-payload="roomEditPayload"
      @createdRecord="runPostApiCallsCleanUps"
      @closeModal="closeModal"
    />
    <UITable
      id="room-list-table"
      :columns="columns"
      :data="roomList.resourceList"
      :page-count="roomList.totalPages"
      :loading="AppState.isLoading || AppState.loadingAssets.rooms"
      :table-layout="'fixed'"
      :enable-text-search="false"
      transparent
      @update:page="x => (roomList.filters.value.page = x)"
    >
      <template #empty>
        <UIEmpty
          v-if="roomList.allResources?.length === 0"
          id="empty-room-title"
          :title="$t('settings.calendar.no_rooms_in_location_title')"
          :description="$t('settings.calendar.no_rooms_in_location')"
          :positive-text="$t('settings.calendar.add_new_calendar_rooms')"
          :icon="CalendarCheck01Icon"
          :positive-icon="PlusIcon"
          @positive-click="openRoomsModal"
        />

        <UIEmpty
          v-else
          id="empty-room-description"
          :title="$t('settings.calendar.no_rooms_in_location_title')"
          :description="$t('settings.calendar.no_rooms_found')"
        />
      </template>
      <template #filter-left>
        <div class="text-[18px] font-semibold text-gray-900">
          {{ t('settings.calendar.rooms') }}
        </div>
      </template>
      <template #filter-right-end>
        <SearchBox
          :search-text="roomList.filters.value.searchText"
          :on-search="onSearch"
          :placeholder="t('settings.calendar.room_name')"
        />
        <UIButton
          id="calendar-settings-rooms-headers-btn-primary"
          type="primary"
          size="small"
          @click="
            () => {
              openRoomsModal(null)
            }
          "
        >
          <div class="flex flex-row items-center gap-2">
            <PlusIcon class="h-5 w-5 text-white" />
            <div class="font-normal">
              {{ $t('settings.calendar.create_rooms') }}
            </div>
          </div>
        </UIButton>
      </template>
    </UITable>

    <DeleteConfirmation
      v-model:show="showDeleteConfirmationModal"
      :disabled-confirm="isProcessing"
      :header-title="$t('settings.calendar.delete_this_rooms')"
      @onConfirm="deleteRoom"
      @onModalClose="closeModal"
    >
      <template #message>
        <p class="text-sm leading-5 text-gray-500">
          {{ $t('settings.calendar.delete_rooms_confirmation') }}
        </p>
      </template>
    </DeleteConfirmation>

    <GeneralConfirmation
      v-model:show="showDisableConfirmationModal"
      :disabled-confirm="isProcessing"
      :header-title="
        $t('settings.calendar.disable_room_title', {
          status: selectedRoom?.isActive ? 'Disable' : 'Activate',
        })
      "
      @onConfirm="disableRoom"
      @onModalClose="closeModal"
    >
      <template #message>
        <p class="text-sm leading-5 text-gray-500">
          {{
            t('settings.calendar.rooms_status_message', {
              status: selectedRoom?.isActive ? 'Disable' : 'Activate',
            })
          }}
        </p>
        <p class="text-sm leading-5 text-gray-500">
          {{ t('settings.calendar.calendar_resource_disclaimer') }}
        </p>
        <br />
        <p
          v-if="selectedRoom?.isActive"
          class="text-sm leading-5 text-gray-500"
        >
          {{ t('settings.calendar.room_disclaimer') }}
        </p>
      </template>
    </GeneralConfirmation>
  </div>
</template>

<style lang="scss">
#roomsListContainer {
  .table-container .n-data-table .n-data-table-td {
    padding: 10px 24px !important;
  }
}
</style>
